/**
 * Firebase Web SDK bootstrap — configured for Phone Auth (SMS OTP) only.
 *
 * The whole app uses ONE Firebase instance. We initialise lazily so unit
 * tests / server-side rendering don't blow up when env vars are missing.
 */
import { initializeApp, getApps } from "firebase/app";
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export function firebaseApp() {
  return getApps()[0] || initializeApp(config);
}

export function firebaseAuth() {
  return getAuth(firebaseApp());
}

/**
 * Create (or reuse) the invisible reCAPTCHA verifier bound to a specific DOM
 * container. Must be called AFTER the container is in the DOM.
 *
 * `containerId` — id of a mounted div. We keep the verifier singleton on
 * `window` so React strict-mode double-effects don't create two verifiers.
 */
export function ensureRecaptcha(containerId = "recaptcha-container") {
  if (typeof window === "undefined") return null;
  const auth = firebaseAuth();
  if (!window.__cargoloadRecaptcha) {
    window.__cargoloadRecaptcha = new RecaptchaVerifier(auth, containerId, {
      size: "invisible",
    });
  }
  return window.__cargoloadRecaptcha;
}

export function resetRecaptcha() {
  try {
    window.__cargoloadRecaptcha?.clear?.();
  } catch { /* noop */ }
  window.__cargoloadRecaptcha = null;
}

/**
 * Kick off the phone-number verification. Returns the ConfirmationResult
 * whose `confirm(otp)` call finishes the flow.
 */
export async function sendOtp(phone, containerId = "recaptcha-container") {
  const verifier = ensureRecaptcha(containerId);
  return signInWithPhoneNumber(firebaseAuth(), phone, verifier);
}
